/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Alert, { AlertColor } from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import LinearProgress from "@mui/material/LinearProgress";
import { isEmpty } from "lodash";
import Button from "@mui/material/Button";
import { CirromLogoAvatar } from "../Logo";
import { ChickenOutModal } from "../utils/ChickenOutModal";
import { describeChangeCount } from "../suggested-changes/utils";
import {
  CandidateValueGroup,
  groupCandidates,
  LedgerField,
  RecordReviewModel,
  rejectUnits,
} from "./helpers/normalize";

import {
  approveCandidate,
  approveCompanyWithAccount,
  CrmAccount,
  preferredCandidate,
  rejectGroup,
  setCompanyAccount,
} from "./helpers/actions";
import {
  AccountFollowOns,
  CompanyResolutionActions,
  requiresAccountResolution,
} from "./CompanyResolution";

import {
  isEmailField,
  isEmailWriteBlocked,
  useEmailVerificationVersion,
} from "./helpers/emailVerification";
import { acceptCompanyAddressLink } from "../../api/cirrommonitor";
import { useUndoableWrite } from "../utils/useUndoableWrite";
import { UndoSnackbar } from "../utils/UndoSnackbar";
import {
  acceptSuggestedChangesWithModifications,
  approveAdHocSuggestedChange,
} from "../../api/suggestedchanges";
import { DossierPane } from "./DossierPane";
import { ChangeLedger } from "./ChangeLedger";
import { FullRecordSection, UnsyncedFieldNote } from "./FullRecordSection";
import { useCrmVendor } from "./helpers/useCrmVendor";
import { useAppliedChanges } from "./helpers/useAppliedChanges";
import {
  findUnsyncedFieldChange,
  useUnsyncedCompany,
} from "./helpers/useUnsyncedCompany";
import { formatAppliedDate } from "./AppliedChangesList";
import { ShareDetails } from "./helpers/shareUtils";
import { ShareCopyActions } from "./ShareCopyActions";
import classes from "./css/recordReview.module.css";

/*
 * The record-review surface: dossier beside ledger, full record beneath.
 *
 * Deliberately a self-contained card taking only data props - like
 * AccountSummaryCard, it is built to be lifted into a modal once this layout
 * replaces the two review modals it is an alternative to.
 *
 * This card writes to the customer's CRM: per-row approve/reject, ad-hoc
 * overwrites from either pane, and the bulk actions in the commit bar. Bulk
 * actions confirm through ChickenOutModal first; per-row writes currently do
 * not, which is a known gap rather than a decision.
 */
export const RecordReviewCard = (props: {
  model: RecordReviewModel;
  integratedSystemId: number;
  recordId: number;

  /**
   * Change id the page was opened for, when it was. Ad-hoc edits cite it when
   * it is there and fall back to the record's own CRM id plus its system when
   * it is not - a record with nothing pending is still one the backend can
   * write to.
   */
  suggestedChangeId?: number | null;
  unavailableSources: string[];

  /**
   * Called after any write settles, so the owner can refetch. Carries the
   * suggested-change ids that were actually decided, which the grid hosting
   * this in a modal uses to drop their rows - a change decided here is no
   * longer pending there.
   */
  onChanged: (decidedChangeIds?: number[]) => void;
  /**
   * Raised while a CRM write is in flight or a bulk confirmation is waiting
   * on an answer. Reported rather than enforced: the card does not own the
   * shell around it, and only a host that can be dismissed out from under a
   * write - a modal - has anything to do about it.
   */
  onBusyChange?: (busy: boolean) => void;
  onRetry?: () => void;
  /* Modal hosting only; the page leaves all three undefined. */
  onClose?: () => void;
  onPreviousRowRequested?: () => void;
  onNextRowRequested?: () => void;
  /**
   * Hosted inside another card (the account summary's Overview tab): no
   * outer Card chrome, no branded header - the host already carries the
   * record's identity - and no share/copy utilities, which are written for a
   * contact being sent to a colleague. The review body, the commit bar and
   * the undo window are the surface, and they behave identically.
   */
  embedded?: boolean;
  /**
   * Embedded hosting only: the host's footer element. When given, the commit
   * bar renders into it beside the host's own utilities, and the card drops
   * its own divider and footer so only one rule sits above the buttons.
   */
  commitActionsContainer?: HTMLElement | null;
}) => {
  const { model } = props;
  const { identity } = model;
  /*
   * Resolved here rather than inside each child: the card is the one place
   * that holds the system id, so one lookup keeps the ledger's "approvals
   * write to ..." line, the full record's edit rule and the dossier's links
   * saying the same vendor's name.
   */
  const crmVendor = useCrmVendor(props.integratedSystemId);
  /*
   * The vendor's name wherever a sentence has to say where a write lands.
   * Neutral for the file and generic system types, which have no vendor to
   * name - "your CRM" is then the truest thing the surface can say.
   */
  const crmName = crmVendor?.label ?? "your CRM";
  const cardRef = useRef<HTMLDivElement>(null);

  /*
   * Applied history rides beside the ledger. Every settled write bumps the
   * key so a change the steward just approved shows up there without a
   * reopen; the ledger keeps the last good list while the refetch runs.
   */
  const [appliedRefreshKey, setAppliedRefreshKey] = useState(0);
  const appliedChanges = useAppliedChanges({
    integratedSystemId: props.integratedSystemId,
    entityType: model.entityType,
    crmId:
      model.entityType === "ACCOUNT" ? model.crmAccountId : model.crmContactId,
    refreshKey: appliedRefreshKey,
  });
  /*
   * A company the CRM now holds and Cirrom's copy has not read back yet. The
   * stored record is left as it is - a batch may still be analysing it - and
   * the surface shows the CRM's value, marked, until the next sync.
   */
  const unsyncedCompany = useUnsyncedCompany({
    integratedSystemId: props.integratedSystemId,
    appliedChanges: model.entityType === "CONTACT" ? appliedChanges : null,
    snapshotBatchId: model.snapshotBatchId,
    crmAccountId: model.crmAccountId,
  });

  const notifyChanged = (decidedChangeIds?: number[]) => {
    setAppliedRefreshKey((key) => key + 1);
    props.onChanged(decidedChangeIds);
  };

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
  const alertTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(
    () => () => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
    },
    []
  );

  /*
   * Success dismisses itself; warnings and errors stay until closed. The
   * pending timer is tracked so a second alert cannot inherit the first one's
   * countdown and vanish early (same discipline as the modal).
   */
  const displayAlert = (text: string, severity: AlertColor) => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = undefined;
    }
    setAlertSeverity(severity);
    setAlertText(text);
    setAlertOpen(true);
    if (severity === "success") {
      alertTimerRef.current = setTimeout(() => setAlertOpen(false), 2500);
    }
  };

  /** The employer to name: the CRM's, where Cirrom has not caught up yet. */
  const displayCompany = unsyncedCompany?.name || identity.company;

  const displayName =
    (model.entityType === "CONTACT"
      ? [identity.firstName, identity.lastName].filter(Boolean).join(" ")
      : identity.company) || "Unnamed record";

  const [requestInProgress, setRequestInProgress] = useState(false);
  const { queued, queue, undo } = useUndoableWrite(props.recordId);

  /*
   * A write can succeed at transport level and still fail in the CRM - the
   * response carries a `failures` map in that case, and reporting success
   * over it would tell the steward a change landed when it did not.
   */
  const handleWriteResponse = (
    response: any,
    successMessage: string,
    decidedChangeIds?: number[]
  ) => {
    if (!isEmpty(response?.failures)) {
      const firstKey = Object.keys(response.failures)[0];
      displayAlert(
        `Unable to change value\n ${response.failures[firstKey]}`,
        "error"
      );
      // Refused by the CRM, so nothing was decided: the row stays.
      notifyChanged();
      return;
    }
    displayAlert(successMessage, "success");
    notifyChanged(decidedChangeIds);
  };

  /*
   * Only Cirrom candidates carry a suggested-change id, and only those have
   * a row in the suggested-changes grid; a Monitor detection decided here
   * was never listed there.
   */
  const decidedIds = (
    candidates: { sourceIds: { suggestedChangeId?: number } }[]
  ) =>
    Array.from(
      new Set(
        candidates
          .map((candidate) => candidate.sourceIds.suggestedChangeId)
          .filter(Boolean) as number[]
      )
    );

  /*
   * Writes reach the CRM one at a time. Committing a decision can overlap the
   * one before it - queueing the next decision commits the last immediately -
   * and the CRM applies overlapping writes to one contact in whatever order
   * they land. A company change followed quickly by an address link from the
   * new account sent the link while the contact was still at the old company.
   * So each write starts only once the previous one has answered, whether it
   * succeeded or not, and the card stays busy until the last has.
   */
  const writeChainRef = useRef<Promise<void>>(Promise.resolve());
  const writesOutstandingRef = useRef(0);
  const enqueueWrite = (task: () => Promise<void>) => {
    writesOutstandingRef.current += 1;
    setRequestInProgress(true);
    writeChainRef.current = writeChainRef.current
      .then(task)
      // Each task reports its own failure; this only keeps the chain moving.
      .catch(() => undefined)
      .finally(() => {
        writesOutstandingRef.current -= 1;
        if (writesOutstandingRef.current === 0) setRequestInProgress(false);
      });
  };

  const runWrite = (
    write: () => Promise<any>,
    successMessage: string,
    failureMessage: string,
    decidedChangeIds?: number[]
  ) =>
    enqueueWrite(() =>
      write()
        .then((response) =>
          handleWriteResponse(response, successMessage, decidedChangeIds)
        )
        .catch((err) => {
          console.error(err);
          displayAlert(failureMessage, "error");
          // Refetch anyway: a partial rejection may have landed one pipeline.
          notifyChanged();
        })
    );

  const handleApproveGroup = (field: LedgerField, group: CandidateValueGroup) =>
    queue({
      key: field.fieldName,
      label: `${field.label} approved`,
      run: () =>
        runWrite(
          () =>
            approveCandidate(preferredCandidate(group), {
              recordId: props.recordId,
              integratedSystemId: props.integratedSystemId,
            }),
          "Change successfully accepted.",
          `That change did not reach ${crmName}. It is still pending.`,
          decidedIds([preferredCandidate(group)])
        ),
    });

  const handleRejectGroup = (field: LedgerField, group: CandidateValueGroup) =>
    queue({
      key: field.fieldName,
      label: `${field.label} rejected`,
      run: () =>
        runWrite(
          () => rejectGroup(group),
          "Change rejected. Cirrom won't suggest it again for this record.",
          "That change could not be rejected. It is still pending.",
          decidedIds(group.candidates)
        ),
    });

  /** Every field the record has, in one list: the ledger's and the rest. */
  const allFields = [...model.ledgerFields, ...model.fullRecordFields];

  /*
   * Ad-hoc overwrite from the full record. A field with a pending Cirrom
   * change goes through accept-with-modifications (the steward is amending
   * the suggestion); a quiet field goes through the ad-hoc endpoint, exactly
   * as the modal splits them.
   */
  const writeAdHoc = (field: LedgerField, newValue: string) => {
    const cirromCandidate = field.candidates.find(
      (candidate) =>
        candidate.source === "cirrom" && candidate.sourceIds.suggestedChangeId
    );
    return cirromCandidate
      ? acceptSuggestedChangesWithModifications(
          cirromCandidate.sourceIds.suggestedChangeId,
          newValue
        )
      : approveAdHocSuggestedChange(
          field.fieldName,
          newValue,
          field.crmValue ?? "",
          {
            suggestedChangeId: props.suggestedChangeId,
            integratedSystemId: props.integratedSystemId,
            ...(model.entityType === "ACCOUNT"
              ? { crmAccountId: model.crmAccountId }
              : { crmContactId: model.crmContactId }),
          },
          field.crmId
        );
  };

  const handleAdHocApprove = (field: LedgerField, newValue: string) =>
    queue({
      key: field.fieldName,
      label: `${field.label} set to "${newValue}"`,
      run: () =>
        runWrite(
          () => writeAdHoc(field, newValue),
          "Change successfully accepted.",
          `That change did not reach ${crmName}. It is still pending.`
        ),
    });

  /*
   * An email derived from the account is a WORK address, so it belongs on the
   * primary field only when that one isn't already holding a personal one.
   * Monitor is the only source that knows which it is; without it the primary
   * is fair game, exactly as the comparison table has it.
   */
  const accountEmailField = allFields.find(
    (field) =>
      field.fieldName ===
      (model.primaryEmailIsPersonal ? "secondary_email" : "primary_email")
  );

  /** A write answered without error can still carry the CRM's refusal. */
  const refusalOf = (response: any): string | undefined =>
    isEmpty(response?.failures)
      ? undefined
      : String(Object.values(response.failures)[0]);

  /*
   * Company, resolved to an account rather than approved as text.
   *
   * The Monitor comparison table has always refused to write a detected
   * employer as a string, because what the CRM stores is a link to an account
   * record - a contact carrying the right company name and no account link
   * looks correct and is useless to everyone downstream. That refusal comes
   * with this surface: the picker replaces the row's approve, and the write
   * routes to whichever pipeline proposed the change.
   *
   * With no group it is the ad-hoc company edit, opened from the pencil in
   * either pane. Nothing proposed it, so nothing is decided by it - it simply
   * links the record to the account the steward chose.
   *
   * A job move is one decision with up to three parts, so the account's email
   * and office ride the same undo window. They are sent strictly after the
   * company and only once it has landed: an office or a work address of the
   * new employer means nothing on a contact still at the old one, and the CRM
   * has refused exactly that ordering.
   */
  const handleApproveAccount = (
    field: LedgerField,
    account: CrmAccount,
    group: CandidateValueGroup | undefined,
    followOns: AccountFollowOns
  ) => {
    const context = {
      recordId: props.recordId,
      integratedSystemId: props.integratedSystemId,
    };
    const emailField = followOns.email ? accountEmailField : undefined;
    /* The same words the picker uses: the target field's name, and "office". */
    const OFFICE = "the office";
    const followOnLabels = [
      emailField ? emailField.label : null,
      followOns.address ? OFFICE : null,
    ].filter(Boolean) as string[];
    const withFollowOns = followOnLabels.length
      ? `, with ${followOnLabels.join(" and ")}`
      : "";
    const linked = `Company linked to ${account.name} in ${crmName}`;

    const writeBundle = async () => {
      let companyRefusal: string | undefined;
      /*
       * The link endpoint answers an empty body when the CRM already holds
       * that account, having sent nothing. Saying "linked" then would claim a
       * write that never happened - which is how a silent no-op once passed
       * for success - so the outcome names what actually went.
       */
      let alreadyLinked = false;
      try {
        const response = await (group
          ? approveCompanyWithAccount(
              preferredCandidate(group),
              account,
              context
            )
          : setCompanyAccount(account, context));
        alreadyLinked = response === "";
        companyRefusal = refusalOf(response);
      } catch (err) {
        console.error(err);
        companyRefusal = `That change did not reach ${crmName}. It is still pending.`;
      }
      // The follow-ons wait on the company, so a refusal means none left.
      const notSent = followOnLabels.length ? " Nothing else was sent." : "";
      if (companyRefusal) {
        displayAlert(`${companyRefusal}${notSent}`, "error");
        // Refused, so nothing was decided: the row stays.
        notifyChanged();
        return;
      }

      // One at a time, in the order the tooltip promised.
      const failed: string[] = [];
      const sendFollowOn = async (label: string, write: () => Promise<any>) => {
        try {
          if (refusalOf(await write())) failed.push(label);
        } catch (err) {
          console.error(err);
          failed.push(label);
        }
      };
      if (emailField && followOns.email) {
        await sendFollowOn(emailField.label, () =>
          writeAdHoc(emailField, followOns.email!)
        );
      }
      if (followOns.address) {
        await sendFollowOn(OFFICE, () =>
          acceptCompanyAddressLink(
            props.recordId,
            props.integratedSystemId,
            followOns.address!.id
          )
        );
      }

      const outcome = alreadyLinked
        ? `Company was already linked to ${account.name} in ${crmName}`
        : linked;
      if (failed.length) {
        // The dangerous case: the record is part-moved. Name the split.
        displayAlert(
          `${outcome}, but ${failed.join(" and ")} didn't reach it.`,
          "warning"
        );
      } else if (alreadyLinked && !followOnLabels.length) {
        displayAlert(`${outcome}. Nothing was sent.`, "info");
      } else {
        displayAlert(`${outcome}${withFollowOns}.`, "success");
      }
      notifyChanged(
        group ? decidedIds([preferredCandidate(group)]) : undefined
      );
    };

    queue({
      key: field.fieldName,
      label: `${field.label} linked to ${account.name}${withFollowOns}`,
      run: () => enqueueWrite(writeBundle),
    });
  };

  /*
   * Without a record and system id there is no account list to search and no
   * endpoint to write to, so the company row offers no approval at all rather
   * than one that would write the name as text. Contacts only: on a COMPANY
   * record the "company" field is the account's own name, and the picker's
   * endpoints link a *contact* to an account - offering it here would write
   * to the wrong record entirely.
   */
  const companyResolution: CompanyResolutionActions | undefined =
    model.entityType === "CONTACT" && props.integratedSystemId && props.recordId
      ? {
          integratedSystemId: props.integratedSystemId,
          recordId: props.recordId,
          emailTarget: accountEmailField
            ? {
                label: accountEmailField.label,
                currentValue: accountEmailField.crmValue ?? "",
                crmContactId: model.crmContactId ?? undefined,
              }
            : undefined,
          onApproveAccount: handleApproveAccount,
          canWriteAccountEmail: model.hasExplainData,
          crmName,
        }
      : undefined;

  /*
   * Bulk actions.
   *
   * Approve all applies every UNCONTESTED change: fields with exactly one
   * proposed value. A contested field is precisely the one a bulk action must
   * not decide, so those are skipped and the confirmation says so. Reject all
   * rejects everything pending, contested included - declining the lot is a
   * coherent single decision in a way that approving it is not.
   *
   * A company is skipped for a different reason: it needs an account only the
   * steward can choose, and there is no unattended write for it that wouldn't
   * put the name in as text.
   */
  const uncontestedGroups = model.ledgerFields
    .map((field) => ({ field, groups: groupCandidates(field.candidates) }))
    .filter(({ groups }) => groups.length === 1 && groups[0].value)
    .map(({ field, groups }) => ({ field, group: groups[0] }))
    .filter(({ field, group }) => !requiresAccountResolution(field, group));
  /*
   * An email Allegrow has refused loses its row approve, so it must lose the
   * bulk one too - otherwise Approve all is the way round the row's rule.
   * The verdict store is read through getters; the version hook is what
   * recomputes this list when a verdict lands.
   */
  useEmailVerificationVersion();
  const isUndeliverable = ({
    field,
    group,
  }: {
    field: LedgerField;
    group: CandidateValueGroup;
  }) => isEmailField(field.fieldName) && isEmailWriteBlocked(group.value);
  const approvableGroups = uncontestedGroups.filter(
    (entry) => !isUndeliverable(entry)
  );
  const skippedUndeliverableCount =
    uncontestedGroups.filter(isUndeliverable).length;
  const skippedContestedCount = model.ledgerFields.filter(
    (field) => groupCandidates(field.candidates).length > 1
  ).length;
  const skipsCompany = model.ledgerFields.some((field) =>
    groupCandidates(field.candidates).some((group) =>
      requiresAccountResolution(field, group)
    )
  );
  /*
   * Reject units rather than value groups: a MultipleChoice change shows one
   * row per option but is a single change, so counting its options separately
   * both overstated the confirmation ("Reject 3 changes" for one change) and
   * posted the same id once per option - and the duplicate refusals came back
   * as changes that had failed to reject when in fact all of them had.
   */
  const rejectableUnits = model.ledgerFields.flatMap((field) =>
    rejectUnits(field.candidates).map((group) => ({ field, group }))
  );

  const [pendingBulkAction, setPendingBulkAction] = useState<
    "approve" | "reject" | null
  >(null);

  /*
   * The queued undo window is deliberately not busy: leaving commits it, which
   * is the documented bargain, so a host that closes mid-window loses nothing.
   * A request already on its way to the CRM is the opposite - there is no undo
   * behind it, and the answer would land in an unmounted card.
   */
  const busy = requestInProgress || pendingBulkAction !== null;
  /* Through a ref so a caller's inline callback cannot re-fire the report. */
  const busyListenerRef = useRef(props.onBusyChange);
  busyListenerRef.current = props.onBusyChange;
  useEffect(() => {
    busyListenerRef.current?.(busy);
    /* Unmounting mid-write must not leave the host guarded forever. */
    return () => {
      if (busy) busyListenerRef.current?.(false);
    };
  }, [busy]);

  /*
   * Bulk still confirms first - the dialog states the scope, which an undo
   * window cannot - and then holds like everything else, so a steward who
   * realises mid-sentence that they picked the wrong record has the same way
   * back as they do on a single row.
   */
  const queueBulk = (action: "approve" | "reject", run: () => void) =>
    queue({
      key: "bulk",
      label:
        action === "approve"
          ? `${describeChangeCount(approvableGroups.length)} approved`
          : `${describeChangeCount(rejectableUnits.length)} rejected`,
      run,
    });

  /** A settled write succeeded only if it resolved without a failures map. */
  const isSettledSuccess = (result: PromiseSettledResult<any>) =>
    result.status === "fulfilled" && isEmpty(result.value?.failures);

  /*
   * `makeCalls` is deferred so the bulk's requests are only sent once any
   * write ahead of it has answered; within the bulk they still go together.
   */
  const runBulk = (
    makeCalls: () => Promise<any>[],
    describe: (ok: number, failed: number, total: number) => void,
    /** Parallel to the calls, so only the ones that settled cleanly are reported. */
    idsPerCall: number[][] = []
  ) =>
    enqueueWrite(() =>
      Promise.allSettled(makeCalls())
        .then((results) => {
          const ok = results.filter(isSettledSuccess).length;
          describe(ok, results.length - ok, results.length);
          const decided = results.flatMap((result, index) =>
            isSettledSuccess(result) ? idsPerCall[index] ?? [] : []
          );
          notifyChanged(decided);
        })
        .catch(() => {
          notifyChanged();
        })
    );

  const approveAll = () =>
    queueBulk("approve", () =>
      runBulk(
        () =>
          approvableGroups.map(({ group }) =>
            approveCandidate(preferredCandidate(group), {
              recordId: props.recordId,
              integratedSystemId: props.integratedSystemId,
            })
          ),
        (ok, failed, total) => {
          if (failed === 0) {
            displayAlert(
              `${describeChangeCount(total)} written to ${crmName}.`,
              "success"
            );
          } else if (ok === 0) {
            displayAlert(
              `None of the ${total} changes reached ${crmName}. Nothing has changed - try again.`,
              "error"
            );
          } else {
            // The dangerous case: the record is now part-actioned. Name the split.
            displayAlert(
              `${ok} of ${total} changes written to ${crmName}. ${failed} did not reach it and are still pending.`,
              "warning"
            );
          }
        }
      )
    );

  const rejectAll = () =>
    queueBulk("reject", () =>
      runBulk(
        () => rejectableUnits.map(({ group }) => rejectGroup(group)),
        (ok, failed, total) => {
          if (failed === 0) {
            displayAlert(
              `${describeChangeCount(
                total
              )} rejected. Cirrom won't suggest them again for this record.`,
              "success"
            );
          } else if (ok === 0) {
            displayAlert(
              `None of the ${total} changes could be rejected. Nothing has changed - try again.`,
              "error"
            );
          } else {
            displayAlert(
              `${ok} of ${total} changes rejected. ${failed} could not be rejected and are still pending.`,
              "warning"
            );
          }
        }
      )
    );

  /*
   * What the full record says about the unsynced company. The company row
   * shows the CRM's account; the groups holding fields read from the old
   * account (its address, its switchboard) are still the old employer's and
   * say so once, at the group. Any other field written to the CRM since the
   * snapshot's read shows the written value the same way, until the next sync.
   */
  const unsyncedFields: Record<string, UnsyncedFieldNote> = {};
  const unsyncedGroups: Record<string, UnsyncedFieldNote> = {};
  if (unsyncedCompany) {
    const previous = identity.company || "the previous company";
    const current = unsyncedCompany.name || "a different account";
    const writtenBy = unsyncedCompany.writtenBy
      ? ` by ${unsyncedCompany.writtenBy}`
      : "";
    unsyncedFields.company = {
      value: unsyncedCompany.name ?? undefined,
      label: "Awaiting sync",
      title: `Written to ${crmName}${writtenBy} on ${formatAppliedDate(
        unsyncedCompany.writtenAt
      )}. Cirrom still holds ${previous} until its next read of ${crmName}.`,
    };
    allFields
      .filter((field) => field.fromAccount && field.group)
      .forEach((field) => {
        unsyncedGroups[field.group!] = {
          label: "May be out of date",
          title: `The fields marked as from the account are ${previous}'s. ${crmName} now links this contact to ${current}, and they update after the next sync.`,
        };
      });
  }
  allFields
    .filter((field) => field.fieldName !== "company")
    .forEach((field) => {
      const change = findUnsyncedFieldChange(
        model.entityType === "CONTACT" ? appliedChanges : null,
        { batchId: model.snapshotBatchId },
        field.fieldName,
        field.crmValue
      );
      if (!change) return;
      const writtenBy = change.user ? ` by ${change.user}` : "";
      unsyncedFields[field.fieldName] = {
        value: change.new_value ?? "",
        label: "Awaiting sync",
        title: `Written to ${crmName}${writtenBy} on ${formatAppliedDate(
          change.last_update_time
        )}. Cirrom still holds ${
          field.crmValue || "no value"
        } until its next read of ${crmName}.`,
      };
    });

  /** Best display value for a field: incumbent CRM value, else first candidate. */
  const fieldDisplayValue = (fieldName: string): string => {
    const field = allFields.find(
      (candidate) => candidate.fieldName === fieldName
    );
    return field?.crmValue || field?.candidates[0]?.value || "";
  };

  const shareDetails: ShareDetails = {
    entity: "contact",
    name: displayName,
    company: displayCompany,
    jobTitle: identity.jobTitle,
    email: fieldDisplayValue("primary_email"),
    phone: fieldDisplayValue("telephone"),
  };

  /*
   * Embedded, the host owns the card chrome, so this renders as a bare
   * region; standalone it is the branded card the modals hand over to.
   */
  const Shell: React.ElementType = props.embedded ? "div" : Card;

  /*
   * The commit bar. Approve all is the surface's single contained
   * (amber) button - the rationed accent lands exactly where the
   * committing decision lives. Reject all stays outlined and neutral:
   * declining suggestions is a steward doing the job, not an error.
   */
  const commitActions = model.ledgerFields.length > 0 && (
    <div className={classes.commitActions}>
      <Button
        size="small"
        variant="outlined"
        disabled={requestInProgress}
        onClick={() => setPendingBulkAction("reject")}
        sx={{
          // MUI's small button drops to 12px; these are the surface's
          // committing controls and hold the 13px base like the rest.
          fontSize: "13px",
          color: "text.primary",
          borderColor: "text.secondary",
          "&:hover": {
            borderColor: "text.primary",
            backgroundColor: "action.hover",
          },
        }}
      >
        Reject all
      </Button>
      {approvableGroups.length > 0 && (
        <Button
          size="small"
          color="primary"
          variant="contained"
          disabled={requestInProgress}
          onClick={() => setPendingBulkAction("approve")}
          sx={{ fontSize: "13px" }}
        >
          Approve all ({approvableGroups.length})
        </Button>
      )}
    </div>
  );

  return (
    <Shell ref={cardRef} className={props.embedded ? undefined : classes.card}>
      {/*
       * Branded header, carried over from the review modals on purpose: this
       * surface gets screenshotted and shared outside the product, and the
       * mark should travel with it. An embedded card skips it - the host's
       * header already names the record.
       */}
      {!props.embedded && (
        <CardHeader
          sx={{ py: 3 }}
          avatar={
            <CirromLogoAvatar
              styleOverrides={{ width: "48px", height: "48px" }}
            />
          }
          title={displayName}
          /*
           * The record's name is the card's heading, not decorative text: it
           * rendered as a span, which left a screen reader nothing to navigate
           * between the page h1 and the ledger.
           */
          titleTypographyProps={{ variant: "h3", component: "h2" }}
          subheader={
            model.entityType === "CONTACT" &&
            (identity.jobTitle || displayCompany) ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {[identity.jobTitle, displayCompany]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            ) : undefined
          }
          /*
           * Only rendered when the card is hosted in a modal. Prev/next carry
           * the queue position the grid knows about, and close is error-tinted
           * for parity with the modal this one stands in for.
           */
          action={
            props.onClose || props.onNextRowRequested ? (
              <div className={classes.headerNav}>
                {props.onClose && (
                  <IconButton
                    onClick={props.onClose}
                    color="error"
                    disabled={busy}
                    aria-label="Close record review"
                  >
                    <CloseOutlinedIcon />
                  </IconButton>
                )}
                {(props.onPreviousRowRequested || props.onNextRowRequested) && (
                  <div className={classes.headerNavButtons}>
                    <IconButton
                      onClick={() => props.onPreviousRowRequested?.()}
                      color="info"
                      disabled={!props.onPreviousRowRequested}
                      aria-label="Previous contact"
                      className={classes.headerNavButton}
                    >
                      <NavigateBefore />
                      <Typography variant="body2">Prev</Typography>
                    </IconButton>
                    <IconButton
                      onClick={() => props.onNextRowRequested?.()}
                      color="info"
                      disabled={!props.onNextRowRequested}
                      aria-label="Next contact"
                      className={classes.headerNavButton}
                    >
                      <NavigateNext />
                      <Typography variant="body2">Next</Typography>
                    </IconButton>
                  </div>
                )}
              </div>
            ) : undefined
          }
        />
      )}
      <Collapse in={alertOpen}>
        <Alert
          severity={alertSeverity}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setAlertOpen(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {alertText}
        </Alert>
      </Collapse>
      {requestInProgress && <LinearProgress />}
      {!props.embedded && <Divider />}
      {/*
       * The only part that scrolls. Hosted in a modal the card is capped to
       * the viewport, and without this the header and the commit bar scrolled
       * away with the content - so the record's identity and the buttons that
       * write to the CRM could both be off screen at the moment of deciding.
       * On the page nothing caps the height, so this simply grows.
       */}
      <div className={classes.cardScrollArea}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
          }}
        >
          <Box
            component="aside"
            sx={{
              borderRight: { xs: "none", md: "1px solid" },
              borderBottom: { xs: "1px solid", md: "none" },
              borderColor: { xs: "divider", md: "divider" },
            }}
          >
            <DossierPane
              model={model}
              integratedSystemId={props.integratedSystemId}
            />
          </Box>
          <section>
            <ChangeLedger
              fields={model.ledgerFields}
              recordId={props.recordId}
              /*
               * The vendor, never the host's name for the system. A tenant
               * names its systems after itself - the demo tenant's HubSpot is
               * called "Cirrom" - so every host that offered a name offered
               * the wrong one, and threading it was worse than having none.
               */
              crmLabel={crmVendor?.label}
              actions={{
                disabled: requestInProgress,
                // The row holding a decision refuses a second one, so a
                // double-click cannot commit the first and queue a duplicate.
                queuedFieldName: queued?.key,
                onApproveGroup: handleApproveGroup,
                onRejectGroup: handleRejectGroup,
                onAdHocApprove: model.hasExplainData
                  ? handleAdHocApprove
                  : undefined,
                companyResolution,
                integratedSystemId: props.integratedSystemId,
                crmContactId: model.crmContactId,
              }}
              unavailableSources={props.unavailableSources}
              onRetry={props.onRetry}
              appliedChanges={appliedChanges}
            />
          </section>
        </Box>
        <Divider />
        {/*
         * The full record shows every field, including ones with pending
         * changes (their incumbent CRM value) - the layout stays identical
         * across records, so a steward's eye always finds a field in the same
         * place. The grouping inside orders them canonically regardless of the
         * ledger's contested-first sort.
         */}
        <FullRecordSection
          fields={[...model.ledgerFields, ...model.fullRecordFields]}
          crmLabel={crmVendor?.label}
          unsynced={unsyncedFields}
          unsyncedGroups={unsyncedGroups}
          actions={
            /*
             * The company picker does not ride the explain payload - it is a
             * record-scoped write - so the full record still offers that one
             * edit when the text pencils have nothing to write through.
             */
            model.hasExplainData || companyResolution
              ? {
                  disabled: requestInProgress,
                  onAdHocApprove: model.hasExplainData
                    ? handleAdHocApprove
                    : undefined,
                  companyResolution,
                  integratedSystemId: props.integratedSystemId,
                  crmContactId: model.crmContactId,
                }
              : undefined
          }
        />
      </div>

      {props.embedded && props.commitActionsContainer
        ? createPortal(commitActions, props.commitActionsContainer)
        : (!props.embedded || model.ledgerFields.length > 0) && (
            <>
              <Divider />

              <div className={classes.cardFooter}>
                {props.embedded ? (
                  <span />
                ) : (
                  <ShareCopyActions
                    targetRef={cardRef}
                    details={shareDetails}
                    onNotify={displayAlert}
                  />
                )}
                {commitActions}
              </div>
            </>
          )}
      {/*
       * Both bulk actions confirm before anything leaves the browser, with
       * the count the action will actually touch - computed from the same
       * lists the handlers use, so the dialog can never disagree with them.
       */}
      <ChickenOutModal
        modalOpen={pendingBulkAction !== null}
        title={
          pendingBulkAction === "reject"
            ? "Reject all changes?"
            : `Write all changes to ${crmName}?`
        }
        message={
          pendingBulkAction === "reject"
            ? `Reject ${describeChangeCount(
                rejectableUnits.length
              )} on this record. Cirrom won't suggest them again for this record.`
            : `Write ${describeChangeCount(
                approvableGroups.length
              )} to ${crmName}. This updates the record other people work from.${
                skippedContestedCount > 0
                  ? ` ${skippedContestedCount} contested field${
                      skippedContestedCount === 1 ? " is" : "s are"
                    } skipped - choose those values individually.`
                  : ""
              }${
                skippedUndeliverableCount > 0
                  ? ` ${skippedUndeliverableCount} email address${
                      skippedUndeliverableCount === 1 ? " is" : "es are"
                    } skipped - Allegrow reports ${
                      skippedUndeliverableCount === 1 ? "it" : "they"
                    } can't be delivered.`
                  : ""
              }${
                skipsCompany
                  ? " Company is skipped too - it needs the account you want the contact linked to."
                  : ""
              }`
        }
        confirmLabel={
          pendingBulkAction === "reject" ? "Reject changes" : "Write changes"
        }
        onModalOpenChange={(open) => {
          if (!open) {
            setPendingBulkAction(null);
          }
        }}
        onSuccess={() => {
          const action = pendingBulkAction;
          setPendingBulkAction(null);
          if (action === "reject") {
            rejectAll();
          } else {
            approveAll();
          }
        }}
      />

      <UndoSnackbar queued={queued} onUndo={undo} />
    </Shell>
  );
};
